import axios from 'axios';

// Determine the base URL based on environment
const BASE_URL = process.env.REACT_APP_API_URL || '';


//console.log("BASE_URL frontend:",BASE_URL);
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 900000, // 15 minutes for large file uploads (increased from 5 minutes)
  withCredentials: true, // Include cookies in requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor - cookies handle authentication automatically
api.interceptors.request.use(
  (config) => {
    // SSO authentication uses cookies only - no need for Authorization header
    // Cookies are sent automatically with withCredentials: true
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor with better error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors (token expired)
    if (error.response && error.response.status === 401) {
      // Check if this is a login endpoint or validation endpoint - don't redirect for these
      const isLoginEndpoint = error.config?.url?.includes('/user_login');
      const isValidationEndpoint = error.config?.url?.includes('validate/') || error.config?.url?.includes('accounts/auth/validate');
      const isSSOREndpoint = error.config?.url?.includes('sso-callback');

      if (isLoginEndpoint || isValidationEndpoint || isSSOREndpoint) {
        // Just return the error without clearing anything or redirecting
        return Promise.reject(error);
      }

      // For other 401 errors, don't clear localStorage or redirect
      // Let the application handle authentication state
      console.log('401 error on protected endpoint - letting app handle it');
    }
    
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout:', {
        url: error.config.url,
        method: error.config.method,
        timeout: error.config.timeout
      });
    } else if (!error.response) {
      console.error('Network error:', {
        url: error.config?.url,
        message: 'No response received from server',
        error: error.message
      });
    } else {
      console.error('API error:', {
        url: error.config.url,
        status: error.response.status,
        data: error.response.data
      });
    }
    return Promise.reject(error);
  }
);

class ApiController {
  static async get(endpoint, params = {}) {
    try {
      const response = await api.get(endpoint, { params });
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async post(endpoint, data = {}) {
    //console.log("POST request to:",endpoint);
    //console.log("Data being sent:", data);
    try {
      const response = await api.post(endpoint, data);
      //console.log("POST request response:", response);
      return response.data;
    } catch (error) {
      // Don't log expected errors like 404 (post not found) or course not found
      if (!(error.response && error.response.status === 404 && 
            (error.response.data?.error_msg === 'Post not found' || 
             error.response.data?.message === 'Course not found or inactive'))) {
        console.error("POST request error:", error);
      }
      ApiController.handleError(error);
      throw error;
    }
  }

  static async put(endpoint, data = {}) {
    try {
      const response = await api.put(endpoint, data);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async patch(endpoint, data = {}) {
    try {
      const response = await api.patch(endpoint, data);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async delete(endpoint) {
    try {
      const response = await api.delete(endpoint);
      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async uploadFile(endpoint, formData, additionalData = {}, onProgress = null) {
    try {
      // Add any additional data to the existing FormData
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      // Use longer timeout for video uploads
      const isVideoUpload = endpoint.includes('/video/upload') || endpoint.includes('video');
      const timeout = isVideoUpload ? 900000 : 300000; // 15 minutes for video, 5 minutes for others

      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: timeout,
        onUploadProgress: onProgress
          ? (progressEvent) => {
              if (progressEvent && progressEvent.total && progressEvent.total > 0) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
              }
            }
          : undefined,
      });

      return response.data;
    } catch (error) {
      console.error('Upload failed:', error.message);
      ApiController.handleError(error);
      throw error;
    }
  }

  static async uploadFileWithMethod(endpoint, formData, method = 'POST', additionalData = {}, onProgress = null) {
    try {
      // Add any additional data to the existing FormData
      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      // Choose the appropriate axios method
      const axiosMethod = method.toLowerCase() === 'put' ? api.put :
                         method.toLowerCase() === 'patch' ? api.patch :
                         api.post;

      const response = await axiosMethod(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 900000, // 15 minutes for video uploads
        onUploadProgress: onProgress
          ? (progressEvent) => {
              if (progressEvent && progressEvent.total && progressEvent.total > 0) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
              }
            }
          : undefined,
      });

      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static async uploadMultipleFiles(endpoint, files, additionalData = {}, onProgress = null) {
    try {
      const formData = new FormData();

      files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });

      Object.keys(additionalData).forEach(key => {
        formData.append(key, additionalData[key]);
      });

      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              onProgress(percentCompleted);
            }
          : undefined,
      });

      return response.data;
    } catch (error) {
      ApiController.handleError(error);
      throw error;
    }
  }

  static handleError(error) {
    // Don't log expected errors like 404 (post not found) or course not found to reduce console noise
    if (error.response && error.response.status === 404 && 
        (error.response.data?.error_msg === 'Post not found' || 
         error.response.data?.message === 'Course not found or inactive')) {
      // This is an expected error, don't log it
      return;
    }
    
    // Don't log 401 errors as they're handled in the interceptor
    if (error.response && error.response.status === 401) {
      return;
    }
    
    if (error.response) {
      console.error('Response Error:', error.response.data);
      console.error('Status:', error.response.status);
      console.error('Headers:', error.response.headers);
    } else if (error.request) {
      console.error('Request Error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
  }
}

export const GET = ApiController.get;
export const GET_BLOB = async (endpoint, params = {}) => {
  const response = await api.get(endpoint, { params, responseType: 'blob' });
  return response;
};
export const POST = ApiController.post;
export const PUT = ApiController.put;
export const PATCH = ApiController.patch;
export const DELETE = ApiController.delete;
export const UPLOAD_FILE = ApiController.uploadFile;
export const UPLOAD_FILE_WITH_METHOD = ApiController.uploadFileWithMethod;
export const UPLOAD_MULTIPLE_FILES = ApiController.uploadMultipleFiles;

export default ApiController;
